import { Meta, theme } from 'aimo-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, Text, View } from 'react-native';
import styled from 'styled-components/native';
import { AddressProps, Maybe } from 'types/generatedSchemaTypes';
import { navigateWithCoordinates } from 'utils/mapUtils';

const MetaShrink = styled(Meta)<{ isLink?: boolean; color?: string }>`
  flex-shrink: 1;
  color: ${(p) => (p.isLink ? theme.colors.boost.green : p.color)};
  text-decoration: ${(p) => (p.isLink ? 'underline' : 'none')};
  text-decoration-color: ${(p) =>
    p.isLink ? theme.colors.boost.green : p.color};
`;

export const printAddress = (
  address: AddressProps,
  defaultName = '',
  includeCity = true
): string => {
  const { street, number, city } = address;
  if (street) {
    const streetPart = `${street} ${number || ''}`;
    const cityPart = city && includeCity ? `, ${city}` : '';
    return `${streetPart}${cityPart}`;
  }
  return defaultName;
};

// Format location text for screen readers to properly announce the comma
// This needs to be used within a component where useTranslation is available
export const formatLocationTextForScreenReader = (
  text: string | null | undefined,
  translationFunction: Function
) => {
  if (!text) {
    return '';
  }
  const decimalPoint = translationFunction('ariaLabels.decimalPoint', {
    defaultValue: 'point',
  });

  return text.replace(/,(\d+)/g, ` ${decimalPoint} $1`);
};

type AddressAndLocationProps = {
  address?: Maybe<AddressProps>;
  color?: string;
  name?: string;
  locationText?: string | null;
  numberOfLines?: number;
  testID?: string;
  addressAsLink?: boolean;
  includeCity?: boolean;
  isStreetZone?: boolean;
};

const AddressAndLocation = ({
  address,
  locationText,
  color,
  name,
  numberOfLines,
  testID,
  addressAsLink = false,
  includeCity,
  isStreetZone = false,
}: AddressAndLocationProps) => {
  const { t } = useTranslation();
  const { marker } = address || {};
  if (isStreetZone) {
    return (
      <MetaShrink
        color={color ?? theme.colors.black}
        accessibilityLabel={`${name}-multiple-addresses`}
        testID={`${testID}-multiple-addresses`}>
        {t('parkingZone.severalAddresses')}
      </MetaShrink>
    );
  }
  const addressString = printAddress(
    address || {},
    t('parkingZone.noAddress'),
    includeCity
  );

  const onPress = () => {
    addressAsLink && navigateWithCoordinates(marker?.coordinates, name);
  };

  const Wrapper = addressAsLink ? Pressable : View;

  // Process location text for screen readers using the translation function
  const screenReaderLocationText = formatLocationTextForScreenReader(
    locationText,
    t
  );

  return (
    <Wrapper onPress={onPress}>
      <MetaShrink
        isLink={addressAsLink}
        color={color ?? theme.colors.black}
        numberOfLines={numberOfLines}
        testID={testID}
        accessibilityLabel={`${addressString}${!!addressString && !!locationText ? ' • ' : ''}${screenReaderLocationText}`}>
        {addressString}
        <Text style={{ color: theme.colors.gray100 }}>
          {!!addressString && !!locationText && ' • '}
        </Text>
        {locationText}
      </MetaShrink>
    </Wrapper>
  );
};

export default AddressAndLocation;
